/**
 * Network Status Indicator Component
 * 
 * Shows network status to users when there are connectivity issues
 * that might affect authentication and data loading.
 */

import React from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { WifiIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface NetworkStatusIndicatorProps {
  showOnlyWhenIssues?: boolean;
}

export const NetworkStatusIndicator: React.FC<NetworkStatusIndicatorProps> = ({
  showOnlyWhenIssues = true
}) => {
  const networkStatus = useNetworkStatus();

  // Don't show if online and fast connection (when showOnlyWhenIssues is true)
  if (showOnlyWhenIssues && networkStatus.isOnline && !networkStatus.isSlowConnection) {
    return null;
  }

  const getStatusColor = () => {
    if (!networkStatus.isOnline) return 'text-red-600 bg-red-50';
    if (networkStatus.isSlowConnection) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getStatusText = () => {
    if (!networkStatus.isOnline) return 'Tidak ada koneksi internet';
    if (networkStatus.isSlowConnection) return 'Koneksi lambat terdeteksi';
    return 'Koneksi stabil';
  };

  const getStatusIcon = () => {
    if (!networkStatus.isOnline || networkStatus.isSlowConnection) {
      return <ExclamationTriangleIcon className="w-4 h-4" />;
    }
    return <WifiIcon className="w-4 h-4" />;
  };

  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium ${getStatusColor()}`}>
      {getStatusIcon()}
      <span>{getStatusText()}</span>
      {networkStatus.effectiveType && (
        <span className="opacity-75">({networkStatus.effectiveType.toUpperCase()})</span>
      )}
    </div>
  );
};